import { MouseEvent, useRef, useState } from 'react';
import { Chip, CircularProgress, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import './CategoryCountSelector.css';

export function CategoryCountSelector<T extends string>({
  options,
  minWidth = '175px',
  activeTab,
  isLoading = false,
  badgeColors,
  numOfCategories,
  getLabel,
  setActiveTab,
}: {
  options: readonly T[];
  minWidth?: string;
  activeTab: T;
  isLoading?: boolean;
  badgeColors: Map<T, string>;
  numOfCategories: Map<T, number>;
  getLabel: (option: T) => string;
  setActiveTab: (tab: T) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectorWidth, setSelectorWidth] = useState<number>();
  const chipRef = useRef<HTMLDivElement>(null);
  const open: boolean = Boolean(anchorEl);

  function handleClick(event: MouseEvent<HTMLElement>): void {
    setSelectorWidth(undefined);
    setAnchorEl(event.currentTarget);
  }

  function handleClose(): void {
    setAnchorEl(null);
  }

  function handleSelect(option: T): void {
    setActiveTab(option);
    handleClose();
  }

  function handleMenuEntering(menuPaper: HTMLElement): void {
    if (!chipRef.current) return;

    setSelectorWidth(
      Math.ceil(
        Math.max(chipRef.current.offsetWidth, chipRef.current.scrollWidth, menuPaper.offsetWidth, menuPaper.scrollWidth)
      )
    );
  }

  return (
    <>
      <Chip
        ref={chipRef}
        className="active-tab-selector"
        onClick={handleClick}
        clickable
        label={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2">{getLabel(activeTab)}</Typography>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
          </Stack>
        }
        icon={
          <div
            className="category-badge-icon"
            style={{
              backgroundColor: badgeColors.get(activeTab),
              color: 'white',
            }}
          >
            {isLoading ? (
              <CircularProgress size={10} />
            ) : (
              <Typography variant="caption">{numOfCategories.get(activeTab)}</Typography>
            )}
          </div>
        }
        sx={{
          borderRadius: open ? '16px 16px 0px 0px' : '16px',
          backgroundColor: 'rgb(146, 146, 146)',
          boxSizing: 'border-box',
          width: selectorWidth,
          minWidth,
          '&:hover': { backgroundColor: 'rgb(170, 170, 170)' },
        }}
        data-qa-element-id="category_selector"
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '0px 0px 16px 16px',
              backgroundColor: 'white',
              boxSizing: 'border-box',
              width: selectorWidth,
              minWidth,
            },
          },
          transition: {
            onEntering: handleMenuEntering,
          },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option}
            value={option}
            onClick={() => handleSelect(option)}
            sx={{
              backgroundColor: 'white',
              '&:hover': { backgroundColor: 'rgb(170, 170, 170)' },
            }}
            data-qa-element-id={`menu_item_${option.toLowerCase()}`}
          >
            <div className="category-badge-list">
              <div className="category-badge-icon" style={{ backgroundColor: badgeColors.get(option) }}>
                <Typography variant="caption">{numOfCategories.get(option)}</Typography>
              </div>
              <Typography variant="body2">{getLabel(option)}</Typography>
            </div>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
